<template>
  <div class="pageRoot linkListPage">
    <div class="pageShell" ref="pageCard">
      <div ref="pageCardMain" class="pageShell__main">
      <div
        ref="scrollArea"
        class="pageShell__scroll"
        :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
      >
        <div
          class="listWorkspace"
          :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
        >
        <!-- 卡片 A：筛选区（Stripe Surface-1） -->
        <section
          ref="filterAnchor"
          class="filterCard filterAnchor"
          :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
        >
          <link-list-filter-panel
            v-bind="filterPanelProps"
            v-on="filterPanelListeners"
          />
        </section>

        <!-- 卡片 B：列表区（控制条 Surface-2 + 表头 Surface-3 + 行 Surface-1） -->
        <section
          class="tableCard tableZone"
          :class="{ 'tableCard--scrolled': controlBarScrolled }"
          ref="tableZone"
        >
          <!--
            表头上方恒定 48px 控制条：左/右锚点固定，中间为动态显示屏；
            滚出筛区后正中插入导航条，不再整层 opacity 切换。
          -->
          <div
            class="scrollControlBar"
            :class="{
              'scrollControlBar--scrolled': controlBarScrolled,
            }"
          >
            <!-- PART-1 左锚点：结果总量 -->
            <div class="scrollControlBar__lead">
              <span class="scrollControlBar__label">共</span>
              <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
              <span class="scrollControlBar__label">条</span>
              <span
                v-if="multipleSelection.length"
                class="scrollControlBar__selected"
              >
                · 已选 {{ multipleSelection.length }}
              </span>
            </div>

            <!-- 未滚动：中间整块显示屏 -->
            <div
              v-if="!controlBarScrolled"
              class="scrollControlBar__stage scrollControlBar__stage--full"
            >
              <control-bar-display
                :layout="controlBarDisplayLayout"
                :scope-filters="activeScopeFilters"
                :has-active-scope-filters="hasActiveScopeFilters"
                :scope-filter-tooltip="scopeFilterTooltip"
                :selection-count="multipleSelection.length"
                @open-filter="openFilterDrawer"
                @batch-tag="openBatchTagDialog('page')"
                @export-selected="exportExcel"
                @clear-selection="clearSelection"
              />
            </div>

            <!-- 滚动后：左显示 | 正中导航 | 右操作/显示 -->
            <template v-else>
              <div class="scrollControlBar__stage scrollControlBar__stage--left">
                <control-bar-display
                  v-if="controlBarShowScrolledInfo"
                  part="info"
                  layout="info"
                  :scope-filters="activeScopeFilters"
                  :has-active-scope-filters="hasActiveScopeFilters"
                  :scope-filter-tooltip="scopeFilterTooltip"
                  :selection-count="multipleSelection.length"
                  @open-filter="openFilterDrawer"
                />
              </div>
              <div class="scrollControlBar__navSlot">
                <control-bar-nav
                  @scroll-top="scrollToTableTop"
                  @open-filter="openFilterDrawer"
                />
              </div>
              <div class="scrollControlBar__stage scrollControlBar__stage--right">
                <control-bar-display
                  v-if="controlBarShowScrolledActions"
                  part="actions"
                  :layout="controlBarDisplayLayout"
                  :scope-filters="activeScopeFilters"
                  :has-active-scope-filters="hasActiveScopeFilters"
                  :selection-count="multipleSelection.length"
                  @batch-tag="openBatchTagDialog('page')"
                  @export-selected="exportExcel"
                  @clear-selection="clearSelection"
                />
              </div>
            </template>

            <!-- PART-5 右锚点：分页上下文 -->
            <div class="scrollControlBar__page">
              <template v-if="listTotalCount > 0">
                <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
              </template>
              <template v-else>
                <span class="scrollControlBar__emptyHint">暂无数据</span>
                <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
              </template>
            </div>
          </div>
        <div
          ref="tableSection"
          class="tableSection"
          v-if="!loading && tableData.length"
        >
          <el-table
          ref="multipleTable"
          class="linkTable linkTable--hybrid"
          :data="tableData"
          tooltip-effect="dark"
          style="width: 100%"
          :row-key="getRowKey"
          :row-class-name="getRowClassName"
          @row-click="handleRowClick"
          @selection-change="handleSelectionChange"
        >
          <el-table-column
            type="selection"
            :width="linkTableColWidth.selection"
            align="center"
            header-align="center"
            :reserve-selection="true"
          />
          <el-table-column
            label="资源"
            prop="resourceName"
            align="left"
            header-align="left"
            class-name="resourceColumn"
            :width="linkTableColWidth.resource"
          >
            <template slot-scope="scope">
              <!-- 缩略图在前、名称在后：一眼对应是哪条资源；名称最多两行，悬停看全名，点击复制 -->
              <div class="resourceCell">
                <div class="previewCell">
                  <!-- el-image：点击放大；不用 lazy（与弹窗内 v-lazy 表格冲突的是 lazy 模式，非 preview） -->
                  <el-image
                    v-if="scope.row.thumbUrl"
                    class="previewCell__img"
                    :src="scope.row.thumbUrl"
                    :preview-src-list="[scope.row.thumbUrl]"
                    fit="cover"
                  >
                    <div slot="placeholder" class="previewCell__slot">
                      <i class="el-icon-loading"></i>
                    </div>
                    <div slot="error" class="previewCell__slot previewCell__slot--error">
                      <i class="el-icon-picture-outline"></i>
                      <span>加载失败</span>
                    </div>
                  </el-image>
                  <div v-else class="previewCell__empty">
                    <i class="el-icon-picture-outline"></i>
                    <span>无预览</span>
                  </div>
                </div>
                <word-copy class="resourceCell__name" :title="scope.row.resourceName">{{ scope.row.resourceName }}</word-copy>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            label="资源类型"
            prop="assetType"
            align="center"
            header-align="center"
            :width="linkTableColWidth.type"
          >
            <template slot-scope="scope">
              <div v-if="scope.row.assetType" class="typeCell">
                <el-tag
                  size="mini"
                  effect="plain"
                  :title="scope.row.assetType"
                  :class="[
                    'typeTag',
                    `typeTag--${getTypeTagVariant(scope.row.assetType)}`,
                  ]"
                >{{ scope.row.assetType }}</el-tag>
                <span
                  v-if="stickerKindLabel(scope.row.stickerKind)"
                  class="typeCell__kind"
                >{{ stickerKindLabel(scope.row.stickerKind) }}</span>
              </div>
              <span v-else class="cellMuted">—</span>
            </template>
          </el-table-column>
          <el-table-column
            label="已关联分类"
            align="center"
            header-align="center"
            :width="linkTableColWidth.category"
            show-overflow-tooltip
            class-name="categoryColumn"
          >
            <template slot-scope="scope">
              <div
                v-if="scope.row.categorySimpleVOList && scope.row.categorySimpleVOList.length"
                class="categoryCell"
              >
                <router-link
                  v-for="item in getVisibleCategories(scope.row.categorySimpleVOList)"
                  :key="item.id"
                  :to="{ path: '/collection/classify/info/' + item.id }"
                  class="categoryCell__link"
                >
                  <el-tag size="mini" effect="plain" class="categoryTag">{{
                    item.name
                  }}</el-tag>
                </router-link>
                <el-tag
                  v-if="getHiddenCategoryCount(scope.row.categorySimpleVOList) > 0"
                  size="mini"
                  effect="plain"
                  class="categoryTag"
                >
                  +{{ getHiddenCategoryCount(scope.row.categorySimpleVOList) }}
                </el-tag>
              </div>
              <span v-else class="cellEmptyPill">未关联</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="defaultLockGroupName"
            label="关联解锁"
            :width="linkTableColWidth.lock"
            align="center"
            header-align="center"
            show-overflow-tooltip
            class-name="lockColumn"
          >
            <template slot-scope="scope">
              <!-- 点击弹出：展示完整解锁配置（只读，同源 API） -->
              <el-popover
                placement="top-start"
                trigger="click"
                width="248"
                popper-class="lockCellPopover"
                transition="lockPanelFade"
                @show="handleLockPopoverShow(scope.row)"
                @hide="handleLockPopoverHide"
              >
                <div class="lockPanel">
                  <div class="lockPanel__head">
                    <span class="lockPanel__headIcon" aria-hidden="true">
                      <i class="el-icon-unlock"></i>
                    </span>
                    <span class="lockPanel__headTitle">解锁详情</span>
                  </div>
                  <div class="lockPanel__section">
                    <span class="lockPanel__label">默认解锁</span>
                    <div
                      v-if="isLockDetailLoading(scope.row.id)"
                      class="lockPanel__skeletonLine"
                    ></div>
                    <span v-else class="lockPanel__value">{{
                      getLockDefaultName(scope.row)
                    }}</span>
                  </div>
                  <div
                    v-if="isLockDetailLoading(scope.row.id)"
                    class="lockPanel__section lockPanel__section--dataset"
                  >
                    <span class="lockPanel__label">数据集覆盖</span>
                    <div class="lockPanel__skeletonLine lockPanel__skeletonLine--short"></div>
                  </div>
                  <template v-else-if="getLockDatasetList(scope.row.id).length">
                    <div class="lockPanel__section lockPanel__section--dataset">
                      <span class="lockPanel__label">
                        数据集覆盖
                        <span class="lockPanel__count">{{
                          getLockDatasetList(scope.row.id).length
                        }}</span>
                      </span>
                      <ul class="lockPanel__list">
                        <li
                          v-for="(item, index) in getLockDatasetList(scope.row.id)"
                          :key="index"
                          class="lockPanel__listItem"
                        >
                          <span class="lockPanel__chip lockPanel__chip--group">{{
                            item.lockGroupName || "—"
                          }}</span>
                          <i class="el-icon-right lockPanel__listArrow"></i>
                          <span class="lockPanel__chip lockPanel__chip--set">{{
                            item.dataSetName || "—"
                          }}</span>
                        </li>
                      </ul>
                    </div>
                  </template>
                  <p
                    v-else-if="isLockDetailReady(scope.row.id)"
                    class="lockPanel__hint"
                  >
                    还没有数据集覆盖
                  </p>
                </div>
                <div
                  slot="reference"
                  class="lockCell lockCell--pill"
                  :class="{ 'lockCell--pill--active': lockPopoverOpenId === scope.row.id }"
                >
                  <span class="lockCell__iconWrap" aria-hidden="true">
                    <i class="el-icon-unlock lockCell__icon"></i>
                  </span>
                  <span class="lockCell__name">{{
                    scope.row.defaultLockGroupName || "—"
                  }}</span>
                </div>
              </el-popover>
            </template>
          </el-table-column>
          <el-table-column
            prop="onOrOff"
            label="状态"
            align="center"
            header-align="center"
            :width="linkTableColWidth.status"
          >
            <template slot-scope="scope">
              <resource-list-status :row="scope.row" />
            </template>
          </el-table-column>

          <el-table-column
            label="操作 / 更新时间"
            label-class-name="linkListActionColumnHead"
            align="center"
            header-align="center"
            :width="linkTableColWidth.action"
            class-name="actionColumn"
          >
            <template slot="header">
              <span class="linkListActionColumnHead__label">操作 / 更新时间</span>
            </template>
            <template slot-scope="scope">
              <div class="linkTableActionStack">
                <div class="linkTableActionCell">
                  <el-button
                    size="small"
                    class="rowActionBtn rowActionBtn--ghost"
                    icon="el-icon-view"
                    @click.stop="goInfo(scope.row.id)"
                    :disabled="isConvertLocked(scope.row.onOrOff)"
                  >
                    查看
                  </el-button>
                  <el-button
                    size="small"
                    class="rowActionBtn rowActionBtn--ghost"
                    icon="el-icon-edit"
                    @click.stop="goEdit(scope.row.id)"
                    :disabled="isConvertLocked(scope.row.onOrOff)"
                  >
                    编辑
                  </el-button>
                  <el-button
                    size="small"
                    class="rowActionBtn rowActionBtn--ghost"
                    icon="el-icon-document-copy"
                    v-check-role
                    @click.stop="copyItem(scope.row)"
                    :title="scope.row.assetType === 'stickers' ? '贴纸先不复制，两条资源会共用同一批云上的图' : undefined"
                    :disabled="scope.row.assetType === 'stickers' || isConvertLocked(scope.row.onOrOff) || copyLoadingId === scope.row.id"
                  >
                    复制
                  </el-button>
                  <el-button
                    size="small"
                    class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                    icon="el-icon-delete"
                    v-check-role
                    @click.stop="deleteResourceRow(scope.row)"
                    :disabled="isConvertLocked(scope.row.onOrOff)"
                  >
                    删除
                  </el-button>
                </div>
                <span
                  v-if="scope.row.updateTime"
                  class="linkTableActionStack__time"
                  :title="`更新于 ${scope.row.updateTime}`"
                >{{ String(scope.row.updateTime).slice(0, 16) }}</span>
              </div>
            </template>
          </el-table-column>
        </el-table>
        </div>
        <!-- G2 Phase 4：空数据态 -->
        <div v-else-if="!loading && !tableData.length" class="tableEmpty">
          <div class="tableEmpty__icon">
            <i class="el-icon-link"></i>
          </div>
          <p class="tableEmpty__title">暂无通用资源</p>
          <p class="tableEmpty__desc">
            当前筛选条件下没有匹配的资源，可调整筛选或
            <span class="tableEmpty__descNowrap">创建资源</span>。
          </p>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-upload2"
            class="actionButton actionButtonQuery"
            @click="goCreate"
          >
            创建资源
          </el-button>
        </div>
        <!-- G2 Phase 4：表格 skeleton -->
        <div v-else class="tableSkeletonWrap">
          <div class="tableSkeletonHeader">
            <div
              v-for="n in 9"
              :key="'h-' + n"
              class="tableSkeletonHeader__cell"
            ></div>
          </div>
          <div
            v-for="n in 6"
            :key="'r-' + n"
            class="tableSkeletonRow"
          >
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--check"></div>
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--name"></div>
            <div class="tableSkeletonRow__cell"></div>
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--thumb"></div>
            <div class="tableSkeletonRow__cell"></div>
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--lock"></div>
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--status"></div>
            <div class="tableSkeletonRow__cell"></div>
            <div class="tableSkeletonRow__cell tableSkeletonRow__cell--action"></div>
          </div>
        </div>

        </section>
        </div>
      </div>

      <div
        v-if="filterDrawerOpen"
        class="filterDrawerLayer"
        role="dialog"
        aria-modal="true"
        aria-label="筛选与操作"
      >
        <div
          class="filterDrawerPanel"
          :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
          :style="drawerPanelStyle"
        >
          <div class="filterDrawerPanel__header">
            <span class="filterDrawerPanel__title">筛选与操作</span>
            <div class="filterDrawerPanel__headerActions">
              <span
                v-if="activeFilterCount"
                class="filterDrawerPanel__chip"
              >
                已筛选 {{ activeFilterCount }} 项
              </span>
              <button
                type="button"
                class="filterDrawerPanel__closeBtn"
                @click="closeFilterDrawer"
              >
                收起
              </button>
            </div>
          </div>
          <div ref="drawerScroll" class="filterDrawerPanel__scroll">
            <link-list-filter-panel
              v-bind="{ ...filterPanelProps, showPanelSummary: true }"
              v-on="filterPanelListeners"
            />
          </div>
        </div>
        <div
          class="filterDrawerBackdrop"
          aria-hidden="true"
          @click="closeFilterDrawer"
          @wheel.prevent
        />
      </div>
      </div>

      <!-- 底栏固定在 pageShell 底部，不随筛区/表格滚动（与最初 pageCard footer 一致） -->
      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第 {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          popper-class="genericResourceDropdown"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          layout="prev, pager, next,sizes,jumper"
          :total="listTotalCount"
          :page-sizes="[30, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
        />
      </div>
    </div>
    <upload-excel-component
      :on-success="uploadExcelSuccess"
      :centerDialogVisible.sync="uploadExcelDialog"
    />
    <batch-upload-dialog
      ref="batchUploadDialog"
      :visible.sync="batchUploadVisible"
      :typeOptions="typeOptions"
      @success="search"
    />
    <batch-tag-dialog
      :visible.sync="batchTagVisible"
      :selection="multipleSelection"
      :filters="batchTagFilters"
      :resource-type="resourceType"
      :category-options="categoryOptions"
      :data-options="dataOptions"
      :status-options="wallpaperStatusOptions"
      :type-options="typeOptions"
      :default-tab="batchTagDefaultTab"
      @success="handleBatchTagSuccess"
      @clear-list-selection="clearSelection"
    />
  </div>
</template>

<script>
import {
  getGenericResourceList,
  getGenericResourceById,
  listAllWallpaperType,
  batchDeleteGenericResource,
  deleteGenericResource,
  copyGenericResource,
} from "../../../api/genericResource";
import {
  exportResourceList,
  importResourceList,
  getAllDataList,
  getResourceAllTags,
  getAllCategory,
} from "../../../api/comment";
import UploadExcelComponent from "../../../components/uploadExcel/index.vue";
import BatchUploadDialog from "./batchUploadDialog.vue";
import BatchTagDialog from "./batchTagDialog.vue";
import ControlBarDisplay from "./ControlBarDisplay.vue";
import ControlBarNav from "./ControlBarNav.vue";
import LinkListFilterPanel from "./LinkListFilterPanel.vue";
import {
  mergePageSelection,
  isRowKeySelected,
  syncTableRowSelection,
} from "./linkSelectionHelper";
import {
  normalizeGenericResourceList,
  toLinkListQueryParams,
  toExportQueryPayload,
} from "./genericResourceAdapter";
import { computeLinkTableColumnWidths } from "./linkTableLayout";
import linkListScrollControlMixin from "./linkListScrollControlMixin";
import ResourceListStatus from "./ResourceListStatus.vue";
import { stickerKindLabel, typeSelectionLabel } from "./resourceTypeFilter";
export default {
  name: "list",
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      copyLoadingId: null,
      resourceName: "",
      wallpaperStatus: "",
      /** 资源类型（字符串子类型）；API 契约字段为 wallpaperType */
      assetType: "",
      typeOptions: [],
      key: "",
      tableData: [],
      currentPage: 1,
      currentPageSize: 30,
      total: 0,
      loading: false,
      wallpaperStatusOptions: [
        { label: "上线", value: "1" },
        { label: "下线", value: "0" },
        { label: "资源处理中", value: "10" },
        { label: "等待重试", value: "12" },
        { label: "资源处理失败", value: "11" },
      ],
      lockDetailCache: {},
      lockDetailLoadingId: null,
      /** 当前打开的解锁 popover 行 id，用于 pill 激活态 */
      lockPopoverOpenId: null,
      allTagList: [],
      tagIds: [],
      resourceType: 21,
      categoryType: "",
      categoryOptions: [],
      dataSetId: "",
      dataOptions: [],
      uploadExcelDialog: false,
      batchUploadVisible: false,
      batchTagVisible: false,
      batchTagDefaultTab: "page",
      multipleSelection: [],
      /** 程序化同步表格勾选时跳过 selection-change，避免重复合并 */
      isSyncingTableSelection: false,
    };
  },
  components: {
    ResourceListStatus,
    UploadExcelComponent,
    BatchUploadDialog,
    BatchTagDialog,
    ControlBarDisplay,
    ControlBarNav,
    LinkListFilterPanel,
  },
  /** 浏览器后退也会切走这个页面，批量上传弹窗随之销毁，在途的上云和落库会中断。 */
  beforeRouteLeave(to, from, next) {
    const dialog = this.$refs.batchUploadDialog;
    if (!this.batchUploadVisible || !dialog || !dialog.hasPendingWork) {
      next();
      return;
    }
    this.$confirm(
      "批量上传还没完成，离开会中断上传，已上云但未落库的文件也会作废，确定离开吗？",
      "离开页面",
      { type: "warning", confirmButtonText: "离开", cancelButtonText: "留下" }
    )
      .then(() => next())
      .catch(() => next(false));
  },
  computed: {
    /** 按 tableZone 宽度与列权重比例分配 9 列像素宽，铺满容器 */
    linkTableColWidth() {
      return computeLinkTableColumnWidths(this.tableContainerWidth);
    },
    batchTagFilters() {
      return {
        resourceName: this.resourceName,
        assetType: this.assetType,
        wallpaperStatus: this.wallpaperStatus,
        categoryType: this.categoryType,
        key: this.key,
        dataSetId: this.dataSetId,
      };
    },
    /** 当前已填写的筛选项数量，用于工具栏轻提示 */
    activeFilterCount() {
      return [
        this.resourceName,
        this.key,
        this.assetType,
        this.categoryType,
        this.wallpaperStatus,
        this.dataSetId,
      ].filter((value) => value !== "" && value !== null && value !== undefined)
        .length;
    },
    /**
     * 列表总条数（含 0）：避免 !total 等 falsy 判断把 0 当成「无数据字段」。
     */
    listTotalCount() {
      const parsed = Number(this.total);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    /** 当前页在全量结果中的起始序号（1-based），无数据时为 0 */
    listRangeStart() {
      if (this.listTotalCount === 0) {
        return 0;
      }
      return (this.currentPage - 1) * this.currentPageSize + 1;
    },
    /** 当前页在全量结果中的结束序号，无数据时为 0 */
    listRangeEnd() {
      if (this.listTotalCount === 0) {
        return 0;
      }
      return Math.min(
        this.currentPage * this.currentPageSize,
        this.listTotalCount
      );
    },
    totalPages() {
      if (this.listTotalCount === 0) {
        return 1;
      }
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize));
    },
    /**
     * 摘要带中间区：仅 assetType / categoryId / dataSetId 三维；
     * 名称、Key、状态等不在此展示，避免挤占左右锚点。
     */
    activeScopeFilters() {
      const chips = [];
      const assetType = String(this.assetType || "").trim();
      if (assetType) {
        chips.push({
          key: "assetType",
          label: "资源类型",
          value: typeSelectionLabel(assetType),
        });
      }
      const categoryId = this.categoryType;
      if (categoryId !== "" && categoryId !== null && categoryId !== undefined) {
        chips.push({
          key: "categoryId",
          label: "资源分类",
          value: this.resolveCategoryLabel(categoryId),
        });
      }
      const dataSetId = this.dataSetId;
      if (dataSetId !== "" && dataSetId !== null && dataSetId !== undefined) {
        chips.push({
          key: "dataSetId",
          label: "数据集",
          value: this.resolveDataSetLabel(dataSetId),
        });
      }
      return chips;
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0;
    },
    /** tooltip 展示完整三维条件（含未截断值） */
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(" · ");
    },
    /** 传给 LinkListFilterPanel 的筛区 props（文档流 + overlay 共用） */
    filterPanelProps() {
      return {
        resourceName: this.resourceName,
        filterKey: this.key,
        assetType: this.assetType,
        categoryType: this.categoryType,
        wallpaperStatus: this.wallpaperStatus,
        dataSetId: this.dataSetId,
        activeFilterCount: this.activeFilterCount,
        total: this.listTotalCount,
        typeOptions: this.typeOptions,
        categoryOptions: this.categoryOptions,
        dataOptions: this.dataOptions,
        wallpaperStatusOptions: this.wallpaperStatusOptions,
      };
    },
    /** 筛区面板事件：字段 sync + 业务动作，两份面板 v-on 复用 */
    filterPanelListeners() {
      return {
        "update:resourceName": (value) => {
          this.resourceName = value;
        },
        "update:filterKey": (value) => {
          this.key = value;
        },
        "update:assetType": (value) => {
          this.assetType = value;
        },
        "update:categoryType": (value) => {
          this.categoryType = value;
        },
        "update:wallpaperStatus": (value) => {
          this.wallpaperStatus = value;
        },
        "update:dataSetId": (value) => {
          this.dataSetId = value;
        },
        search: this.search,
        "reset-and-search": this.resetAndSearch,
        "reset-filters": this.resetFilters,
        "go-create": this.goCreate,
        "open-batch-upload": () => {
          this.batchUploadVisible = true;
        },
        "open-upload-excel": () => {
          this.uploadExcelDialog = true;
        },
        "export-excel": this.exportExcel,
        "batch-tag": () => {
          this.openBatchTagDialog("page");
        },
        "batch-delete": this.batchDelete,
      };
    },
  },
  created() {
    this.bindLinkListScrollControl();
    this.getList();
    this.getAllDataSet();
    this.allTag();
    this.getCategory();
    this.getTypeOptions();
  },
  watch: {
    tableContainerWidth() {
      this.scheduleTableLayout();
    },
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered();
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth();
        });
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight();
        this.updateScrollControls();
        this.scheduleTableLayout();
      });
    },
    "multipleSelection.length"() {
      if (this.batchTagVisible) {
        return;
      }
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight();
        this.updateScrollControls();
        if (this.filterDrawerOpen) {
          this.syncDrawerLayout();
        }
      });
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout();
        });
      }
    },
  },
  methods: {
    stickerKindLabel,
    getRowKey(row) {
      return row.id;
    },
    /**
     * 资源类型 pill 语义色：常见类型走固定映射，其余按类型名 hash 落盘。
     * 色板为暖灰适配 muted pastel（见 linkSemanticPills.css）；绿色仅保留给状态 pill。
     */
    getTypeTagVariant(typeName) {
      const raw = String(typeName || "").trim();
      if (!raw) {
        return "neutral";
      }
      const key = raw.toLowerCase();
      if (/recipe/.test(key)) {
        return "amber";
      }
      if (/puzzle|level/.test(key)) {
        return "sky";
      }
      if (/视频|video|mp4|mov/.test(key)) {
        return "video";
      }
      if (/图片|图像|image|photo|wallpaper|静态/.test(key)) {
        return "image";
      }
      if (/链接|link|url|跳转|web|网页|coupon|优惠券/.test(key)) {
        return "link";
      }
      if (/音频|audio|music|白噪音/.test(key)) {
        return "audio";
      }
      if (/live|动态|gif|animation/.test(key)) {
        return "motion";
      }
      const palette = ["sky", "violet", "amber", "rose", "teal", "indigo"];
      let hash = 0;
      for (let i = 0; i < raw.length; i += 1) {
        hash = raw.charCodeAt(i) + ((hash << 5) - hash);
      }
      return palette[Math.abs(hash) % palette.length];
    },
    /** 处理中和等待重试仍保持原有操作限制。 */
    isConvertLocked(onOrOff) {
      return onOrOff === 10 || onOrOff === 12;
    },
    /** 分类列最多展示 2 个 tag，其余折叠为 +N */
    getVisibleCategories(list) {
      return (list || []).slice(0, 2);
    },
    getHiddenCategoryCount(list) {
      return Math.max(0, (list || []).length - 2);
    },
    handleSelectionChange(pageSelected) {
      if (this.isSyncingTableSelection) {
        return;
      }
      this.multipleSelection = mergePageSelection(
        this.multipleSelection,
        this.tableData,
        pageSelected
      );
      // getRowClassName 触发行重绘后，el-table 勾选态可能丢失；按 key 再同步一次视觉态
      this.$nextTick(() => {
        this.syncTableSelectionFromStore();
      });
    },
    /** 分页/刷新后按 multipleSelection 恢复当前页 checkbox 视觉态 */
    syncTableSelectionFromStore() {
      const table = this.$refs.multipleTable;
      if (!table || !this.tableData.length) {
        return;
      }
      this.isSyncingTableSelection = true;
      syncTableRowSelection(table, this.tableData, this.multipleSelection);
      this.$nextTick(() => {
        this.isSyncingTableSelection = false;
      });
    },
    /** 选中行 class：左侧 accent 条 + 浅底，跨页保留时翻页仍可见 */
    getRowClassName({ row }) {
      return isRowKeySelected(this.multipleSelection, row.key)
        ? "linkTable__row--selected"
        : "";
    },
    /**
     * 点击行空白区域切换勾选（链接/按钮/预览图区域不触发，避免误操作）。
     */
    handleRowClick(row, _column, event) {
      if (!event || !event.target) {
        return;
      }
      const target = event.target;
      if (
        target.closest(
          "a, button, .el-button, .el-image, img, .el-checkbox, .lockCell, .el-tag"
        )
      ) {
        return;
      }
      const table = this.$refs.multipleTable;
      if (!table) {
        return;
      }
      table.toggleRowSelection(row);
    },
    exportExcel() {
      const keyList = this.multipleSelection.map((item) => {
        return item.key;
      });
      var resData = toExportQueryPayload(
        this.resourceType,
        {
          resourceName: this.resourceName,
          assetType: this.assetType,
          wallpaperStatus: this.wallpaperStatus,
          categoryType: this.categoryType,
          key: this.key,
          dataSetId: this.dataSetId,
        },
        keyList
      );
      exportResourceList(resData)
        .then((res) => {
          let blob = new Blob([res.data], {
            type: "application/vnd.ms-excel",
          });
          let href = window.URL.createObjectURL(blob);
          let downloadElement = document.createElement("a");
          downloadElement.href = href;
          downloadElement.download =
            decodeURI(
              res.headers["content-disposition"].split("filename=")[1]
            ) || "";
          document.body.appendChild(downloadElement);
          downloadElement.click();
          document.body.removeChild(downloadElement);
          window.URL.revokeObjectURL(href);
          let that = this;
          that.clearSelection();
        })
        .catch((err) => {
          this.$message({
            type: "info",
            message: err,
            showClose: true,
            duration: 5000,
          });
        });
    },
    openBatchTagDialog(defaultTab) {
      this.batchTagDefaultTab = defaultTab;
      this.batchTagVisible = true;
    },
    batchDelete() {
      const idList = this.multipleSelection.map((item) => item.id);
      if (!idList.length) {
        this.$message.warning("请先勾选要删除的资源");
        return;
      }
      this.$confirm("是否确定批量删除？", "提示", { type: "warning" })
        .then(() => batchDeleteGenericResource({ ids: idList }))
        .then((res) => {
          if (res.success) {
            this.$message.success("批量删除成功");
            this.clearSelection();
            this.getList();
          }
        })
        .catch(() => {});
    },
    deleteResourceRow(row) {
      if (!row || row.id == null) {
        this.$message.warning("资源 ID 缺失，无法删除");
        return;
      }
      this.$confirm("此操作将删除该资源，是否继续？", "提示", { type: "warning" })
        .then(() => deleteGenericResource({ id: row.id }))
        .then((res) => {
          if (res && res.success) {
            this.$message.success("删除成功");
            this.multipleSelection = this.multipleSelection.filter(
              (item) => item.key !== row.key
            );
            this.getList();
          }
        })
        .catch(() => {});
    },
    copyItem(row) {
      const id = row && row.id != null ? row.id : row;
      if (row && row.assetType === "stickers") {
        return;
      }
      if (id == null) {
        this.$message.warning("资源 ID 缺失，无法复制");
        return;
      }
      if (this.copyLoadingId != null) {
        return;
      }
      this.$confirm("此操作将复制该资源，是否继续？", "提示", { type: "warning" })
        .then(() => {
          this.copyLoadingId = id;
          return copyGenericResource({ id });
        })
        .then((res) => {
          if (res && res.success) {
            this.$message.success("复制成功");
            this.getList();
          } else if (res) {
            this.$message.error(
              res.errMessage || res.message || "复制失败"
            );
          }
        })
        .catch(() => {})
        .finally(() => {
          this.copyLoadingId = null;
        });
    },
    clearSelection() {
      this.multipleSelection = [];
      if (this.$refs.multipleTable) {
        this.$refs.multipleTable.clearSelection();
      }
    },
    handleBatchTagSuccess() {
      this.clearSelection();
      this.getList();
    },
    /** @deprecated 保留供旧调用 */
    handleBatchTagRemoveResource(row) {
      const key = row && row.key;
      if (!key) {
        return;
      }
      this.multipleSelection = this.multipleSelection.filter(
        (item) => item.key !== key
      );
      if (this.$refs.multipleTable && this.tableData) {
        const tableRow = this.tableData.find((item) => item.key === key);
        if (tableRow) {
          this.$refs.multipleTable.toggleRowSelection(tableRow, false);
        }
      }
    },
    checkedElementCheck() {
      this.syncTableSelectionFromStore();
    },
    uploadExcelSuccess({ results, header }, radio, file) {
      // 查找
      if (radio == "1") {
        if (header.includes("key")) {
          this.$message({
            type: "success",
            message: "导入成功",
          });
          this.multipleSelection = results;
          this.checkedElementCheck();
        } else {
          this.$message({
            type: "info",
            message: "必须包含“key”字段～",
            showClose: true,
            duration: 5000,
          });
        }
      } else {
        // 覆盖 刷新列表
        let form = new FormData();
        form.append("file", file);
        form.append("resourceType", this.resourceType);
        importResourceList(form)
          .then((res) => {
            if (res.data.success) {
              this.$message({
                type: "success",
                message: "导入成功",
              });
              this.getList();
              this.$nextTick(() => {});
              let that = this;
              setTimeout(function () {
                that.multipleSelection = [];
              }, 100);
            } else {
              this.$message({
                type: "info",
                message: res.data.errMessage,
                showClose: true,
                duration: 5000,
              });
            }
          })
          .catch((err) => {
            this.$message({
              type: "info",
              message: err,
              showClose: true,
              duration: 5000,
            });
          });
      }
    },
    getCategory() {
      getAllCategory("", this.resourceType)
        .then((res) => {
          if (res.success) {
            this.categoryOptions = res.data;
          }
        })
        .catch((err) => {
          this.$message({
            type: "info",
            message: err,
            showClose: true,
            duration: 5000,
          });
        });
    },
    getTypeOptions() {
      listAllWallpaperType().then((res) => {
        if (res.success && res.data) {
          this.typeOptions = res.data;
        }
      });
    },
    resetFilters() {
      this.resourceName = "";
      this.assetType = "";
      this.wallpaperStatus = "";
      this.categoryType = "";
      this.dataSetId = "";
      this.key = "";
    },
    /** 筛选 chip「清除」：重置并立即刷新列表 */
    resetAndSearch() {
      this.resetFilters();
      this.search();
    },
    goCreate() {
      this.$router.push({ path: "/resource/link/create" });
    },
    //获取所有的数据集
    getAllDataSet() {
      getAllDataList().then((res) => {
        if (res.success) {
          this.dataOptions = res.data;
        }
      });
    },
    //重新获取列表数据
    getList() {
      this.loading = true;
      // 列表刷新后清缓存，避免解锁配置与行数据不一致
      this.lockDetailCache = {};
      this.lockDetailLoadingId = null;
      getGenericResourceList(
        toLinkListQueryParams({
          pageNum: this.currentPage,
          pageSize: this.currentPageSize,
          resourceName: this.resourceName,
          wallpaperStatus: this.wallpaperStatus,
          assetType: this.assetType,
          categoryId: this.categoryType,
          key: this.key,
          dataSetId: this.dataSetId,
        })
      )
        .then((res) => {
          if (res.success) {
            const parsedTotal = Number(res.data?.total);
            this.total = Number.isFinite(parsedTotal) ? parsedTotal : 0;
            this.tableData = normalizeGenericResourceList(
              res.data?.wallpaperVOList
            );
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.measureTableZoneWidth();
            this.scheduleTableLayout();
          });
        });
    },
    //header-依据查询条件进行查询
    search() {
      this.currentPage = 1;
      this.getList();
    },
    //footer-大小改变
    handleSizeChange(val) {
      this.currentPageSize = val;
      this.$nextTick(() => {
        this.getList();
      });
    },
    //footer-当前页数改变
    handleCurrentChange(val) {
      this.currentPage = val;
      this.getList();
    },
    // 点击弹出解锁详情：打开时懒加载，并标记 pill 激活态
    handleLockPopoverShow(row) {
      this.lockPopoverOpenId = row.id;
      this.ensureLockDetail(row);
    },
    handleLockPopoverHide() {
      this.lockPopoverOpenId = null;
    },
    // 懒加载解锁详情（与原 showLockType 弹窗同源接口，只读展示）
    ensureLockDetail(row) {
      const id = row.id;
      if (this.lockDetailCache[id] || this.lockDetailLoadingId === id) {
        return;
      }
      this.lockDetailLoadingId = id;
      getGenericResourceById({ id })
        .then((res) => {
          if (res.success) {
            this.$set(this.lockDetailCache, id, {
              defaultLockGroupName: res.data.defaultLockGroupName,
              wallpaperLockGroupVOList: res.data.wallpaperLockGroupVOList || [],
            });
          }
        })
        .finally(() => {
          if (this.lockDetailLoadingId === id) {
            this.lockDetailLoadingId = null;
          }
        });
    },
    isLockDetailLoading(id) {
      return this.lockDetailLoadingId === id && !this.lockDetailCache[id];
    },
    isLockDetailReady(id) {
      return !!this.lockDetailCache[id];
    },
    getLockDefaultName(row) {
      const cached = this.lockDetailCache[row.id];
      if (cached && cached.defaultLockGroupName) {
        return cached.defaultLockGroupName;
      }
      return row.defaultLockGroupName || "—";
    },
    getLockDatasetList(id) {
      const cached = this.lockDetailCache[id];
      if (!cached || !cached.wallpaperLockGroupVOList) {
        return [];
      }
      return cached.wallpaperLockGroupVOList;
    },
    //去info页面
    goInfo(id) {
      this.$router.push(`/resource/link/info/${id}`)
    },
    goEdit(id) {
      this.$router.push(`/resource/link/edit/${id}`)
    },
    allTag() {
      getResourceAllTags({ wallpaperType: 20 }).then((res) => {
        if (res.success) {
          this.allTagList = res.data;
        }
      });
    },
  },
};
</script>

<style scoped>
@import './linkListTheme.css';
@import './linkActionButtons.css';

.categoryCell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* 解锁 popover：暖灰舒适色系，低饱和不刺眼 */
.lockPanel {
  padding: 12px 14px 13px;
  background: var(--link-surface-muted);
  font-family: var(--link-font-family);
  font-size: var(--link-text-sm);
  line-height: 1.5;
  color: var(--link-ink-tertiary);
}

.lockPanel__head {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 10px;
}

.lockPanel__headIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  background: var(--link-workspace);
  color: var(--link-ink-tertiary);
  font-size: var(--link-text-xs);
}

.lockPanel__headTitle {
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-semibold);
  color: var(--link-ink-secondary);
}

.lockPanel__section {
  padding: 9px 10px;
  border-radius: 8px;
  background: var(--link-surface);
  border: 1px solid #ececea;
}

.lockPanel__section--dataset {
  margin-top: 8px;
}

.lockPanel__label {
  display: block;
  margin-bottom: 4px;
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-medium);
  color: var(--link-ink-muted);
}

.lockPanel__value {
  color: var(--link-ink-emphasis);
  font-size: var(--link-text-sm);
  font-weight: var(--link-font-medium);
  word-break: break-all;
}

.lockPanel__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  margin-left: 4px;
  border-radius: 999px;
  background: var(--link-workspace);
  color: var(--link-ink-tertiary);
  font-size: var(--link-text-xs);
  font-weight: var(--link-font-semibold);
  vertical-align: middle;
}

.lockPanel__list {
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
  max-height: 108px;
  overflow-y: auto;
}

.lockPanel__listItem {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
}

.lockPanel__listItem + .lockPanel__listItem {
  border-top: 1px solid var(--link-line);
}

.lockPanel__chip {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: var(--link-text-xs);
  line-height: 1.4;
}

.lockPanel__chip--group {
  color: var(--link-ink-secondary);
  background: var(--link-workspace);
}

.lockPanel__chip--set {
  color: var(--link-ink-tertiary);
  background: var(--link-surface-muted);
  border: 1px solid #ececea;
}

.lockPanel__listArrow {
  flex-shrink: 0;
  font-size: var(--link-text-xs);
  color: var(--link-ink-faint);
}

.lockPanel__hint {
  margin: 8px 0 0;
  padding: 7px 10px;
  border-radius: 8px;
  background: var(--link-workspace);
  font-size: var(--link-text-xs);
  color: var(--link-ink-muted);
  text-align: center;
}

.lockPanel__skeletonLine {
  height: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--link-workspace) 25%, var(--link-surface-muted) 50%, var(--link-workspace) 75%);
  background-size: 200% 100%;
  animation: lockPanelShimmer 1.2s ease-in-out infinite;
}

.lockPanel__skeletonLine--short {
  width: 55%;
}

@keyframes lockPanelShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* linkTableActionCell 已迁入 linkTableHybrid.css，供 Ai 角色等同族列表共用 */

::v-deep .el-pagination__jump {
  margin-left: -5px;
}
</style>

<style>
.lockCellPopover {
  padding: 0 !important;
  border-radius: 14px !important;
  overflow: hidden;
}

.lockCellPopover .popper__arrow {
  display: none;
}

.lockPanelFade-enter-active {
  transition: opacity 0.18s ease, transform 0.2s ease;
}

.lockPanelFade-leave-active {
  transition: opacity 0.14s ease, transform 0.14s ease;
}

.lockPanelFade-enter,
.lockPanelFade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
